// src/components/ui/PeriodSelector/PeriodSelector.tsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Flex } from '@/components/ui/Layout';
import DateRangePicker from '@/components/ui/DatePicker/DateRangePicker';
import { useResponsive } from '@/hooks/useResponsive';
import styles from './PeriodSelector.module.css';

// Типы периодов, доступные в приложении
export type Period = 'day' | 'week' | 'month' | 'quarter' | 'year' | 'custom';

// Режимы селектора
export type SelectorMode = 'standard' | 'custom';

// Типы стандартных периодов
export type StandardPeriodType = 'today' | 'byDay' | 'byMonth';

// Настройки для стандартного периода
export interface StandardPeriodConfig {
  periodType: StandardPeriodType;
  month?: number; // 0-11
  year: number;
}

// Интерфейс для диапазона дат
export interface DateRange {
  startDate: Date;
  endDate: Date;
}

export interface PeriodSelectorProps {
  /** Текущий выбранный период */
  period: Period;
  
  /** Обработчик изменения периода */
  onChange: (period: Period, dateRange?: DateRange) => void;
  
  /** Показывать заголовок */
  showTitle?: boolean;
  
  /** Дополнительные CSS классы */
  className?: string;
  
  /** Отключить выбор произвольного периода */
  disableCustom?: boolean;
  
  /** Пространство имен для переводов (по умолчанию 'common') */
  translationNamespace?: string;
  
  /** Показывать кнопку "Сегодня" */
  showTodayButton?: boolean;
  
  /** Переопределение названий кнопок */
  buttonLabels?: {
    byDay?: string;
    byMonth?: string;
  };
  
  /** Активный тип периода по умолчанию */
  defaultPeriodType?: StandardPeriodType;
}

// Дефолтные значения месяцев на случай проблем с i18n
const DEFAULT_MONTHS = ["Январь", "Февраль", "Март", "Апрель", "Май", "Июнь", 
  "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"];

/**
 * Создает дату с фиксированным временем в локальной временной зоне
 */
function createDateWithCorrectTime(year: number, month: number, day: number, isStartOfDay: boolean): Date {
  const date = new Date(year, month, day);
  
  if (isStartOfDay) {
    // Начало дня (00:00:00.000)
    date.setHours(0, 0, 0, 0);
  } else {
    // Конец дня (23:59:59.999)
    date.setHours(23, 59, 59, 999);
  }
  
  return date;
}

/**
 * Универсальный компонент для выбора периода
 * 
 * Используется для выбора стандартных периодов (день, месяц, год)
 * или произвольного периода с помощью календаря
 */
const PeriodSelector: React.FC<PeriodSelectorProps> = ({ 
  onChange, 
  showTitle = true,
  className = '',
  disableCustom = false,
  translationNamespace = 'common',
  showTodayButton = true,
  buttonLabels = {},
  defaultPeriodType = 'today'
}) => {
  const { t } = useTranslation([translationNamespace, 'common']);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  useResponsive();
  
  // Состояние для режима селектора (стандартный или произвольный)
  const [mode, setMode] = useState<SelectorMode>('standard');
  
  // Состояние для стандартного режима
  const [standardConfig, setStandardConfig] = useState<StandardPeriodConfig>(() => {
    const now = new Date();
    return {
      periodType: defaultPeriodType,
      year: now.getFullYear(),
      month: now.getMonth()
    };
  });
  
  // Состояние для произвольного периода
  const [customRange, setCustomRange] = useState<DateRange>(() => {
    const today = new Date();
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
    
    return {
      startDate: oneMonthAgo,
      endDate: today
    };
  });
  
  // Функция для преобразования стандартной конфигурации в тип периода и диапазон дат
  const mapConfigToDateRange = (config: StandardPeriodConfig): { 
    period: Period, 
    dateRange?: DateRange 
  } => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    
    switch (config.periodType) {
      case 'today': {
        // День (сегодня)
        const today = new Date();
        const year = today.getFullYear();
        const month = today.getMonth();
        const day = today.getDate();
        
        return {
          period: 'day',
          dateRange: {
            startDate: createDateWithCorrectTime(year, month, day, true),
            endDate: createDateWithCorrectTime(year, month, day, false)
          }
        };
      }
      case 'byDay': {
        // Месяц (выбранный месяц и год или текущий)
        const year = config.year || currentYear;
        const month = config.month !== undefined ? config.month : currentMonth;
        
        // Получаем последний день месяца
        const lastDay = new Date(year, month + 1, 0).getDate();
        
        return {
          period: 'month',
          dateRange: {
            startDate: createDateWithCorrectTime(year, month, 1, true),
            endDate: createDateWithCorrectTime(year, month, lastDay, false)
          }
        };
      }
      case 'byMonth': {
        // Год (выбранный год или текущий)
        const year = config.year || currentYear;
        
        return {
          period: 'year',
          dateRange: {
            startDate: createDateWithCorrectTime(year, 0, 1, true),
            endDate: createDateWithCorrectTime(year, 11, 31, false)
          }
        };
      }
      default:
        return { period: 'day' };
    }
  };
  
  // Обновляем период при изменении конфигурации
  useEffect(() => {
    if (mode === 'standard') {
      const { period: mappedPeriod, dateRange } = mapConfigToDateRange(standardConfig);
      
      onChange(mappedPeriod, dateRange);
    } else {
      // Убеждаемся, что выбранный диапазон имеет правильное время
      if (!customRange.startDate || !customRange.endDate) {
        console.error('Invalid custom date range');
        return;
      }
      
      // Копируем даты для избежания мутации исходных объектов
      const startDate = new Date(customRange.startDate);
      const endDate = new Date(customRange.endDate);
      
      // Устанавливаем правильное время для начала и конца дня
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);
      
      onChange('custom', {
        startDate: startDate,
        endDate: endDate
      });
    }
  }, [mode, standardConfig, customRange, onChange]);
  
  // Обработчик переключения режима
  const handleModeChange = (newMode: SelectorMode) => {
    setMode(newMode);
  };
  
  // Обработчик изменения типа стандартного периода
  const handleStandardTypeChange = (periodType: StandardPeriodType) => {
    setStandardConfig(prev => ({
      ...prev,
      periodType
    }));
  };
  
  // Обработчик изменения месяца
  const handleMonthChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const monthValue = parseInt(event.target.value);
    setStandardConfig(prev => ({
      ...prev,
      month: monthValue
    }));
  };
  
  // Обработчик изменения года
  const handleYearChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const yearValue = parseInt(event.target.value);
    setStandardConfig(prev => ({
      ...prev,
      year: yearValue
    }));
  };
  
  // Обработчик изменения произвольного периода
  const handleCustomRangeChange = (range: DateRange) => {
    // Проверка на валидность дат
    if (!range.startDate || !range.endDate || 
        isNaN(range.startDate.getTime()) || 
        isNaN(range.endDate.getTime())) {
      console.error('Invalid date range provided:', range);
      return;
    }
    
    setCustomRange(range);
  };
  
  // Генерация списка лет (от текущего - 10 до текущего + 2)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 13 }, (_, i) => currentYear - 10 + i);
  
  // Получение списка локализованных названий месяцев
  let monthsNames: string[] = DEFAULT_MONTHS;
  try {
    const translatedMonths = t('formats.months', { returnObjects: true });
    if (Array.isArray(translatedMonths) && translatedMonths.length === 12) {
      monthsNames = translatedMonths as string[];
    } else if (typeof translatedMonths === 'object' && translatedMonths !== null) {
      const monthsArray = Object.values(translatedMonths);
      if (monthsArray.length === 12) {
        monthsNames = monthsArray.map(item => String(item));
      }
    }
  } catch (error) {
    console.warn('Failed to get translated months, using defaults', error);
  }
  
  // Генерация текста выбранного периода
  
  return (
    <Card shadow="sm" padding="md" className={`${className} ${styles.container}`}>
      <Flex direction="column" gap="md">
        {/* Заголовок, отображаемый только если showTitle=true */}
        {showTitle && (
          <div className="text-lg font-medium mb-2">
            {t('periods.select_period')}
          </div>
        )}
        
        {/* Переключатель режимов */}
        <Flex gap="xs" align="center">
          <div className={styles.toggleGroup} role="group">
            <button
              type="button"
              className={`${styles.toggleButton} ${mode === 'standard' ? styles.active : ''} ${styles.roundedLeft}`}
              onClick={() => handleModeChange('standard')}
            >
              {t('periods.standard')}
            </button>
            {!disableCustom && (
              <button
                type="button"
                className={`${styles.toggleButton} ${mode === 'custom' ? styles.active : ''} ${styles.roundedRight}`}
                onClick={() => handleModeChange('custom')}
              >
                {t('periods.custom')}
              </button>
            )}
          </div>
        </Flex>
        
        {/* Стандартный режим */}
        {mode === 'standard' && (
          <Flex direction="column" gap="sm">
            {/* Кнопки выбора типа периода и селекторы в одной строке */}
            <div className={styles.periodOptions}>
              <div className={styles.periodButtons}>
                {showTodayButton && (
                  <button
                    className={`${styles.periodButton} ${standardConfig.periodType === 'today' ? styles.active : ''}`}
                    onClick={() => handleStandardTypeChange('today')}
                  >
                    {t('periods.today')}
                  </button>
                )}
                <button
                  className={`${styles.periodButton} ${standardConfig.periodType === 'byDay' ? styles.active : ''}`}
                  onClick={() => handleStandardTypeChange('byDay')}
                >
                  {buttonLabels.byDay || t('periods.byDay')}
                </button>
                <button
                  className={`${styles.periodButton} ${standardConfig.periodType === 'byMonth' ? styles.active : ''}`}
                  onClick={() => handleStandardTypeChange('byMonth')}
                >
                  {buttonLabels.byMonth || t('periods.byMonth')}
                </button>
              </div>
              
              {/* Селекторы месяца и года рядом с кнопками */}
              {(standardConfig.periodType === 'byDay' || standardConfig.periodType === 'byMonth') && (
                <div className={styles.selectors}>
                  {/* Селектор месяца */}
                  {standardConfig.periodType === 'byDay' && (
                    <div className={styles.selectGroup}>
                      <label className={styles.selectLabel}>
                        {t('date_picker.month')}
                      </label>
                      <select
                        value={standardConfig.month}
                        onChange={handleMonthChange}
                        className={styles.select}
                      >
                        {monthsNames.map((name, index) => (
                          <option key={index} value={index}>
                            {name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  
                  {/* Селектор года */}
                  <div className={styles.selectGroup}>
                    <label className={styles.selectLabel}>
                      {t('date_picker.year')}
                    </label>
                    <select
                      value={standardConfig.year}
                      onChange={handleYearChange}
                      className={styles.select}
                    >
                      {yearOptions.map(year => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>
          </Flex>
        )}
        
        {/* Произвольный режим */}
        {mode === 'custom' && !disableCustom && (
          <div className={styles.dateRangeWrapper}>
            <DateRangePicker
              startDate={customRange.startDate}
              endDate={customRange.endDate}
              onChange={handleCustomRangeChange}
              className={styles.dateRangePicker}
            />
          </div>
        )}   

      </Flex>
    </Card>
  );
};

export default PeriodSelector;